import { Link } from "react-router-dom";
import { Apple, Heart, Instagram, Linkedin, Play, Twitter, Youtube } from "lucide-react";
import { Reveal } from "./Reveal";

const gold = "#C4A36A";

const COLUMNS = [
  {
    heading: "Legal",
    links: [
      { label: "Privacy Policy", to: "/privacy", id: "privacy" },
      { label: "Terms of Use", to: "/terms", id: "terms" },
      { label: "Cookie Policy", id: "cookie-policy", action: "cookies" },
      { label: "Refunds", to: "/refund-policy", id: "refund-policy" },
      { label: "Legal Requests", to: "/legal-requests", id: "legal-requests" },
    ],
  },
  {
    heading: "Company",
    links: [
      { label: "About Us", to: "/about", id: "about" },
      { label: "Pricing", to: "/pricing", id: "pricing" },
      { label: "Contact", to: "/contact", id: "contact" },
    ],
  },
  {
    heading: "Safety",
    links: [
      { label: "Safety Center", to: "/safety", id: "safety" },
      { label: "Guidelines", to: "/acceptable-use", id: "aup" },
      { label: "Report a Concern", to: "/report-and-appeals", id: "report-and-appeals" },
    ],
  },
];

const SOCIALS = [
  { id: "home", label: "Community Hearts", to: "/", Icon: Heart },
  { id: "instagram", label: "Instagram", Icon: Instagram },
  { id: "twitter", label: "Twitter / X", Icon: Twitter },
  { id: "youtube", label: "YouTube", Icon: Youtube },
  { id: "linkedin", label: "LinkedIn", Icon: Linkedin },
];

const openCookies = () => window.dispatchEvent(new CustomEvent("ch:open-cookie-banner"));

const HeartMark = ({ cx, cy, s = 1 }) => (
  <path
    transform={`translate(${cx} ${cy}) scale(${s}) translate(-6 -7)`}
    d="M6 12.4S1.4 9.2 1.4 5.7C1.4 3.6 3 2.2 4.8 2.2c1.1 0 1.8.6 1.2 1.6C6.2 2.8 6.9 2.2 8 2.2c1.8 0 3.4 1.4 3.4 3.5 0 3.5-4.6 6.7-5.4 6.7z"
    fill={gold}
  />
);

const Crest = () => (
  <svg viewBox="0 0 120 120" className="h-[6.25rem] w-[6.25rem]" aria-hidden="true">
    <path
      d="M16 74c2-22 16-40 32-48"
      fill="none"
      stroke={gold}
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M104 74c-2-22-16-40-32-48"
      fill="none"
      stroke={gold}
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path d="M14 68c7-2 10-10 6-16 6 3 9 10 6 16z" fill={gold} />
    <path d="M106 68c-7-2-10-10-6-16-6 3-9 10-6 16z" fill={gold} />
    <path d="M24 48c7-6 8-14 2-20 8 2 13 10 10 20z" fill={gold} />
    <path d="M96 48c-7-6-8-14-2-20-8 2-13 10-10 20z" fill={gold} />
    <path d="M40 30c3-8 0-16-6-20 9 0 16 8 16 18z" fill={gold} />
    <path d="M80 30c-3-8 0-16 6-20-9 0-16 8-16 18z" fill={gold} />
    <path d="M22 86c6 10 16 18 26 22" fill="none" stroke={gold} strokeWidth="1.5" strokeLinecap="round" />
    <path d="M98 86c-6 10-16 18-26 22" fill="none" stroke={gold} strokeWidth="1.5" strokeLinecap="round" />
    <path
      d="M60 102c0 0-30-20-30-44 0-13 9-22 20-22 6 0 10 3.2 10 8.4C60 39.2 64 36 70 36c11 0 20 9 20 22 0 24-30 44-30 44z"
      fill="none"
      stroke={gold}
      strokeWidth="1.8"
    />
    <HeartMark cx="60" cy="56" s="1.15" />
    <HeartMark cx="48" cy="48" s="0.78" />
    <HeartMark cx="72" cy="48" s="0.78" />
    <HeartMark cx="53" cy="70" s="0.78" />
    <HeartMark cx="67" cy="70" s="0.78" />
  </svg>
);

const linkClass =
  "font-serif text-[15px] leading-relaxed text-[#A08448] transition-colors duration-300 hover:text-[#7A5A14]";

const FooterLink = ({ item }) => {
  if (item.action === "cookies") {
    return (
      <button
        type="button"
        onClick={openCookies}
        data-testid="footer-privacy-choices"
        className={`${linkClass} cursor-pointer text-left`}
      >
        {item.label}
      </button>
    );
  }

  return (
    <Link to={item.to} data-testid={`footer-link-${item.id}`} className={linkClass}>
      {item.label}
    </Link>
  );
};

export const Footer = () => (
  <footer className="relative z-[1] bg-[#FDF8F2]" data-testid="footer">
    <div className="mx-auto max-w-6xl px-6 md:px-10">
      <div className="h-px bg-[#C4A36A]" />

      <Reveal>
        <div className="grid gap-12 py-16 sm:grid-cols-2 lg:grid-cols-[1fr_repeat(3,0.75fr)_1.55fr] lg:items-start lg:gap-6 lg:py-20">
          <div>
            <Link to="/" className="inline-flex flex-col items-start" data-testid="footer-logo">
              <Crest />
              <span className="mt-3 font-serif text-[13px] font-medium uppercase tracking-[0.28em] text-[#C4A36A]">
                Community Hearts
              </span>
            </Link>
            <p className="mt-2 max-w-[12rem] text-[13px] leading-relaxed text-[#6B5A48]">
              Stronger together. Kinder world.
            </p>
          </div>

          {COLUMNS.map((col) => (
            <div key={col.heading} data-testid={`footer-col-${col.heading.toLowerCase()}`}>
              <p className="text-[12px] font-medium uppercase tracking-[0.2em] text-[#C4A36A]">{col.heading}</p>
              <ul className="mt-5 space-y-2.5">
                {col.links.map((item) => (
                  <li key={item.id}>
                    <FooterLink item={item} />
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div data-testid="footer-col-socials">
            <p className="text-[12px] font-medium uppercase tracking-[0.2em] text-[#C4A36A]">Follow us</p>
            <div className="mt-5 flex flex-nowrap gap-2" data-testid="footer-socials">
              {SOCIALS.map(({ id, label, Icon, to }) =>
                to ? (
                  <Link
                    key={id}
                    to={to}
                    aria-label={label}
                    data-testid={`footer-social-${id}`}
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#C4A36A] text-[#C4A36A] transition-colors duration-300 hover:bg-[#C4A36A]/10"
                  >
                    <Icon className="h-4 w-4" />
                  </Link>
                ) : (
                  <button
                    key={id}
                    type="button"
                    title={`${label} — coming soon`}
                    aria-label={label}
                    data-testid={`footer-social-${id}`}
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#C4A36A] text-[#C4A36A] transition-colors duration-300 hover:bg-[#C4A36A]/10 active:scale-95"
                  >
                    <Icon className="h-4 w-4" />
                  </button>
                )
              )}
            </div>

            <div className="mt-6 flex flex-wrap gap-3" data-testid="hero-store-badges">
              <div
                className="flex items-center gap-2.5 rounded-xl border border-[#E2D3B4] bg-white px-3.5 py-2 shadow-[0_8px_20px_-16px_rgba(90,70,40,0.35)]"
                data-testid="hero-app-store-badge"
              >
                <Apple className="h-6 w-6 text-[#2A2118]" />
                <div className="text-left leading-tight">
                  <p className="text-[8px] text-[#6B5A48]">Download on the</p>
                  <p className="text-sm font-semibold text-[#2A2118]">App Store</p>
                </div>
              </div>
              <div
                className="flex items-center gap-2.5 rounded-xl border border-[#E2D3B4] bg-white px-3.5 py-2 shadow-[0_8px_20px_-16px_rgba(90,70,40,0.35)]"
                data-testid="hero-google-play-badge"
              >
                <Play className="h-5 w-5 fill-[#2A2118] text-[#2A2118]" />
                <div className="text-left leading-tight">
                  <p className="text-[8px] uppercase tracking-wide text-[#6B5A48]">Get it on</p>
                  <p className="text-sm font-semibold text-[#2A2118]">Google Play</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Reveal>

      <div className="h-px bg-[#C4A36A]" />
      <p
        data-testid="footer-copyright"
        className="py-8 text-center text-[13px] text-[#8C7040]"
      >
        © 2026 Community Hearts. All rights reserved.
      </p>
    </div>
  </footer>
);
